import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable()
export class AuditoriaService {
    private urlModulo = `${environment.urlAPI}/auditoria`;

    constructor(private http: HttpClient) { }

    /**
     * Recupera a lista de Auditorias Fase II
     * @returns Promise com a lista de auditorias
     */
    public getListaAuditorias(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-lista`)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.auditoria);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera a lista de Auditorias (Fornecedores) em Fase I
     * @returns Promise com a lista de auditorias em fase I
     */
    public getListaAuditoriasFaseI(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-listaFaseI`)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.auditoria);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera os documentos de um fornecedor na fase I da auditoria
     * @param codUsuario Código do usuário
     * @param codWorkflow Código do workflow
     * @returns Promise com os documentos do fornecedor
     */
    public getDocumentosFornecedorFaseI(codUsuario: number, codWorkflow: number): Promise<any> {
        let params = new HttpParams()
            .set('codUsuario', codUsuario.toString())
            .set('codWorkflow', codWorkflow ? codWorkflow.toString() : null);

        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-documentosFornecedorFaseI`, { params })
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.documentos);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera os documentos da fase I da auditoria
     * @returns Promise com os documentos da fase I
     */
    public getDocumentosFaseI(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-documentosFaseI`)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.documentos);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera os endereços de um fornecedor
     * @param codUsuario Código do usuário
     * @param codWorkflow Código do workflow
     * @returns Promise com os endereços do fornecedor
     */
    public getEnderecosFornecedor(codUsuario: number, codWorkflow: number): Promise<any> {
        let params = new HttpParams()
            .set('codUsuario', codUsuario.toString())
            .set('codWorkflow', codWorkflow.toString());
        
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-enderecosEscopoAuditoria`, { params })
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.enderecos);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera os itens selecionados do checklist por código do workflow
     * @param codWorkflow Código do workflow
     * @returns Promise com os itens do checklist
     */
    public getItensChecklistByCodWorkflow(codWorkflow: number): Promise<any> {
        const params = new HttpParams().set('codWorkflow', codWorkflow.toString());
        
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-itensChecklistByCodWorkflow`, { params })
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.checklist);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Busca a equipe de auditoria por código do workflow
     * @param codWorkflow Código do workflow
     * @returns Promise com a equipe de auditoria
     */
    public getEquipeAuditoriaByCodWorkflow(codWorkflow: number): Promise<any> {
        const params = new HttpParams().set('codWorkflow', codWorkflow.toString());
        
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-equipeAuditoria`, { params })
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera dados da auditoria fase II por código
     * @param codAuditoria Código da auditoria (opcional)
     * @param codWorkflow Código do workflow (opcional)
     * @returns Promise com os dados da auditoria fase II
     */
    public getDadosAuditoriaFaseIIByCodigo(codAuditoria?: number, codWorkflow?: number): Promise<any> {
        let params = new HttpParams();
        
        if (codAuditoria) {
            params = params.set('codAuditoria', codAuditoria.toString());
        } else if (codWorkflow) {
            params = params.set('codWorkflow', codWorkflow.toString());
        }
        
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-dadosAuditoriaByCodigo`, { params })
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.dados);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Atualiza o tipo de certificado
     * @param payload Dados do certificado
     * @returns Promise com o resultado da operação
     */
    public atualizaTipoCertificado(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.urlModulo}-atualizaTipoCertificado`, payload)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Grava os endereços e escopo da auditoria
     * @param payload Dados dos endereços e escopo
     * @returns Promise com o resultado da operação
     */
    public gravaEnderecosEscopo(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.urlModulo}-gravaEnderecoEscopo`, payload)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Grava os dados da auditoria fase II
     * @param payload Dados da auditoria
     * @returns Promise com o resultado da operação
     */
    public gravaDadosAuditoriaFaseII(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.urlModulo}-gravaAuditoriaFaseII`, payload)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Grava os requisitos da auditoria
     * @param payload Dados dos requisitos
     * @returns Promise com o resultado da operação
     */
    public gravaRequisitosAuditoria(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.urlModulo}-gravaRequisitosAuditoria`, payload)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Grava a indicação de auditor
     * @param codUsuario Código do usuário
     * @param codWorkflow Código do workflow
     * @returns Promise com o resultado da operação
     */
    public gravaIndicacaoAuditor(codUsuario: number, codWorkflow: number): Promise<any> {
        const payload = {
            codUsuario: codUsuario,
            codWorkflow: codWorkflow
        };
        
        return new Promise((resolve, reject) => {
            this.http.post(`${this.urlModulo}-gravaIndicacaoAuditor`, payload)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Grava a execução da auditoria fase II para um item de requisito
     * @param payload Dados da execução
     * @returns Promise com o resultado da operação
     */
    public gravaExecucaoAuditoriaFaseIIitemRequisito(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.urlModulo}-gravaExecucaoAuditoriaFaseIIitemRequisito`, payload)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Grava a execução da auditoria fase II
     * @param payload Dados da execução
     * @returns Promise com o resultado da operação
     */
    public gravaExecucaoAuditoriaFaseII(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.urlModulo}-gravaExecucaoAuditoriaFaseII`, payload)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Atualiza a data do evento de auditoria
     * @param payload Dados da atualização
     * @returns Promise com o resultado da operação
     */
    public atualizaDataEventoAuditoria(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.post(`${this.urlModulo}-atualizaDataEventoAuditoria`, payload)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera a lista de auditorias para relatório
     * @param filtro Filtro opcional
     * @returns Promise com a lista de auditorias
     */
    public getListaAuditoriasRelatorio(filtro?: string): Promise<any> {
        let params: HttpParams = null;
        
        if (filtro) {
            params = new HttpParams().set('filtro', filtro);
        }
        
        return new Promise((resolve, reject) => {
            const options = params ? { params } : {};
            
            this.http.get(`${this.urlModulo}-listaAuditoriasRelatorio`, options)
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data.dados);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }

    /**
     * Recupera a auditoria fase I de um fornecedor
     * @param uidFornecedor ID do fornecedor
     * @returns Promise com os dados da auditoria
     */
    public getAuditoriaFaseIFornecedor(uidFornecedor: number): Promise<any> {
        const params = new HttpParams().set('uid', uidFornecedor.toString());
        
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlModulo}-recuperarAuditoriaFaseIFornecedor`, { params })
                .subscribe({
                    next: (response: any) => {
                        if (response.type === true) {
                            resolve(response.data);
                        } else {
                            reject(response.data);
                        }
                    },
                    error: (error) => {
                        reject(error);
                    }
                });
        });
    }
}


// WEBPACK FOOTER //
// /app/src/app/services/auditoria.service.ts